import { ResourceLanguage } from "i18next";
import i18n from "PFCore/i18n";
import { LanguageResourceKey } from "PFCore/i18n/resources";

export type OverrideGlobMap = Record<string, () => Promise<{ default: ResourceLanguage }>>;

const LOCALE_OVERRIDE_GLOBS: Record<keyof typeof LanguageResourceKey, OverrideGlobMap> = {
  en: import.meta.glob<{ default: ResourceLanguage }>("../../../../config/i18n/en.*/*.json"),
  frCA: import.meta.glob<{ default: ResourceLanguage }>("../../../../config/i18n/frCA.*/*.json"),
  frFR: import.meta.glob<{ default: ResourceLanguage }>("../../../../config/i18n/frFR.*/*.json"),
  esES: import.meta.glob<{ default: ResourceLanguage }>("../../../../config/i18n/esES.*/*.json"),
  noNO: import.meta.glob<{ default: ResourceLanguage }>("../../../../config/i18n/noNO.*/*.json"),
  ptPT: import.meta.glob<{ default: ResourceLanguage }>("../../../../config/i18n/ptPT.*/*.json"),
  deDE: import.meta.glob<{ default: ResourceLanguage }>("../../../../config/i18n/deDE.*/*.json"),
  fiFI: import.meta.glob<{ default: ResourceLanguage }>("../../../../config/i18n/fiFI.*/*.json"),
  daDK: import.meta.glob<{ default: ResourceLanguage }>("../../../../config/i18n/daDK.*/*.json"),
  svSE: import.meta.glob<{ default: ResourceLanguage }>("../../../../config/i18n/svSE.*/*.json"),
  jaJP: import.meta.glob<{ default: ResourceLanguage }>("../../../../config/i18n/jaJP.*/*.json")
};

const OVERRIDE_PATH_REGEX = /i18n\/[a-zA-Z]+\.([a-zA-Z0-9_-]+)\/([a-zA-Z0-9_-]+)\.json$/;

const parseOverridePath = (path: string): { codename: string; namespace: string } | null => {
  const match = path.match(OVERRIDE_PATH_REGEX);
  if (!match) {
    return null;
  }
  return { codename: match[1], namespace: match[2] };
};

export const loadClientTranslationOverrides = async (
  clientCodename: string | null | undefined,
  overrideGlobs: Record<keyof typeof LanguageResourceKey, OverrideGlobMap> = LOCALE_OVERRIDE_GLOBS
): Promise<void> => {
  if (!clientCodename) {
    return;
  }

  const loadPromises = Object.entries(overrideGlobs).flatMap(([globKey, globMap]) =>
    Object.entries(globMap).flatMap(([path, loadFn]) => {
      const parsed = parseOverridePath(path);
      if (!parsed || parsed.codename !== clientCodename) {
        return [];
      }

      const i18nLocale = LanguageResourceKey[globKey];
      if (!i18nLocale) {
        return [];
      }

      return [
        loadFn()
          .then(({ default: data }) => {
            i18n.addResourceBundle(i18nLocale, parsed.namespace, data, true, true);
          })
          .catch((err) => {
            console.warn(`[i18n] Failed to load override for ${parsed.namespace}:`, err);
          })
      ];
    })
  );

  await Promise.all(loadPromises);
};
